import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { BotonEstadoPublicacion, BotonReenviarStock } from '../components/AccionesPublicacionMl';
import { DataTable } from '../components/DataTable';
import { Badge } from '../components/Badge';
import { useToast } from '../components/Toast';
import { api, extractErrorMessage } from '../api/client';
import { Button } from '../components/Button';
import { StockSku, type ResumenStockSku } from '../components/StockSku';

interface PublicacionItem {
  id: number;
  mlaId: string;
  variantId?: string | null;
  skuInterno?: string | null;
  /** El SKU vinculado sincroniza stock con Mercado Libre. */
  skuSincroniza?: boolean;
  titulo?: string | null;
  categoryId?: string | null;
  categoria?: { id: string; nombre: string } | null;
  precio?: number | null;
  precioPromo?: number | null;
  promoNombre?: string | null;
  promoVence?: string | null;
  stockDisponibleMl?: number | null;
  soldQuantity?: number | null;
  stockMaximo?: number | null;
  stockSeguridad?: number | null;
  pausadaPorSistema?: boolean | null;
  status?: string | null;
  health?: number | null;
  listingTypeId?: string | null;
  activo?: boolean | null;
  descripcion?: string | null;
  sincronizadaCon?: string | null;
  /** Stock del SKU vinculado (null si la publicación no tiene SKU). */
  stock?: ResumenStockSku | null;
}

interface PublicacionInactiva {
  skuInterno: string;
  descripcion: string | null;
  mlaId: string | null;
  activasEnMl: number;
}

const ESTADO_ML_TONES: Record<string, 'green' | 'orange' | 'slate'> = {
  active: 'green',
  paused: 'orange',
  closed: 'slate',
};

const ESTADO_ML_LABELS: Record<string, string> = {
  active: 'Activa',
  paused: 'Pausada',
  closed: 'Cerrada',
};

function downloadCSV(data: any[], filename: string) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(';'));
  const csv = [headers.join(';'), ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Inline-editable cell for per-publication stock settings: the cap (stockMaximo,
 * empty = sin límite) or the safety stock (stockSeguridad, empty = 0).
 */
function StockMaximoCell({ row, onUpdate, campo = 'stockMaximo' }: { row: PublicacionItem; onUpdate: () => void; campo?: 'stockMaximo' | 'stockSeguridad' }) {
  const [editing, setEditing] = useState(false);
  const actual = campo === 'stockMaximo' ? row.stockMaximo : row.stockSeguridad;
  const [value, setValue] = useState(actual != null ? String(actual) : '');
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { can } = useAuth();

  const handleSave = async () => {
    const trimmed = value.trim();
    const parsed = trimmed === '' ? (campo === 'stockSeguridad' ? 0 : null) : Number(trimmed);
    if (trimmed !== '' && (isNaN(parsed as number) || (parsed as number) < 0 || !Number.isInteger(parsed))) {
      toast.addToast('Ingresá un número entero positivo o dejalo vacío', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/v3/ml/publicaciones/${row.id}`, { [campo]: parsed });
      toast.addToast(campo === 'stockMaximo' ? 'Tope guardado' : 'No publicar guardado', 'success');
      onUpdate();
    } catch {
      toast.addToast('Error al guardar', 'error');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          disabled={saving}
          className="w-16 px-1 py-0.5 text-xs border rounded"
          autoFocus
        />
        <button onClick={handleSave} disabled={saving} className="text-green-600 hover:text-green-800 text-xs">✓</button>
        <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { if (can('catalogo', 'editar')) setEditing(true); }}
      disabled={!can('catalogo', 'editar')}
      title={can('catalogo', 'editar')
        ? (campo === 'stockMaximo' ? 'Click para editar el tope: máximo de unidades a publicar' : 'Click para editar las unidades que no se publican')
        : 'No tenés permiso para editar'}
      className="text-left text-xs hover:bg-slate-50 rounded px-1 py-0.5 -mx-1 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {campo === 'stockSeguridad'
        ? (row.stockSeguridad ? row.stockSeguridad : <span className="text-slate-300">0</span>)
        : (row.stockMaximo != null ? row.stockMaximo : <span className="text-slate-300">∞</span>)}
    </button>
  );
}

// Solapa Publicaciones de Base y publicaciones: carga al montarse.
export function PublicacionesMl() {
  const toast = useToast();
  const PUB_PAGE_SIZE = 50;
  const [publicaciones, setPublicaciones] = useState<PublicacionItem[] | null>(null);
  const [pubTotal, setPubTotal] = useState(0);
  const [pubPage, setPubPage] = useState(0);
  const [loadingPublicaciones, setLoadingPublicaciones] = useState(false);
  const [errorPublicaciones, setErrorPublicaciones] = useState<string | null>(null);

  const [pubSearch, setPubSearch] = useState('');
  const [pubSearchInput, setPubSearchInput] = useState('');

  const fetchPublicacionesPage = useCallback(async (page: number, search?: string) => {
    setLoadingPublicaciones(true);
    setErrorPublicaciones(null);
    const q = search !== undefined ? search : pubSearch;
    try {
      const params = new URLSearchParams({ limit: String(PUB_PAGE_SIZE), offset: String(page * PUB_PAGE_SIZE) });
      if (q) {
        const upper = q.trim().toUpperCase();
        if (upper.startsWith('MLA')) {
          params.set('mlaId', q.trim());
        } else if (/^\d+$/.test(q.trim())) {
          params.set('mlaId', `MLA${q.trim()}`);
        } else {
          params.set('q', q.trim());
        }
      }
      const { data, headers } = await api.getWithResponse<PublicacionItem[]>(`/api/v3/ml/publicaciones?${params}`);
      setPublicaciones(data ?? []);
      setPubTotal(Number(headers.get('X-Total-Count') ?? data?.length ?? 0));
      setPubPage(page);
    } catch (e: any) {
      setErrorPublicaciones(extractErrorMessage(e));
    } finally {
      setLoadingPublicaciones(false);
    }
  }, [pubSearch]);

  const refetchPublicaciones = useCallback(() => fetchPublicacionesPage(pubPage), [fetchPublicacionesPage, pubPage]);

  const [exportingAll, setExportingAll] = useState(false);

  const handleExportAllPublicaciones = useCallback(async () => {
    setExportingAll(true);
    try {
      const params = new URLSearchParams();
      if (pubSearch) {
        const upper = pubSearch.trim().toUpperCase();
        if (upper.startsWith('MLA')) params.set('mlaId', pubSearch.trim());
        else if (/^\d+$/.test(pubSearch.trim())) params.set('mlaId', `MLA${pubSearch.trim()}`);
        else params.set('q', pubSearch.trim());
      }
      const rows: any[] = await api.get(`/api/v3/ml/publicaciones/export?${params}`);
      if (!rows?.length) { toast.addToast('Sin datos para exportar', 'error'); return; }
      downloadCSV(rows, `publicaciones-${new Date().toISOString().slice(0,10)}.csv`);
    } catch (e: any) {
      toast.addToast('Error al exportar: ' + (e?.message ?? ''), 'error');
    } finally {
      setExportingAll(false);
    }
  }, [pubSearch, toast]);

  const handlePubSearch = () => {
    setPubSearch(pubSearchInput);
    fetchPublicacionesPage(0, pubSearchInput);
  };

  useEffect(() => {
    fetchPublicacionesPage(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: publicacionesInactivas } = useApi<PublicacionInactiva[]>(
    '/api/v3/ml/publicaciones/inactivas'
  );
  const [syncingPublicaciones, setSyncingPublicaciones] = useState(false);
  const [publicacionesProgress, setPublicacionesProgress] = useState<{ processed: number; total: number; competing: number; pct: number; etaSec: number | null; elapsedSec: number; running: boolean; error: string | null } | null>(null);
  const [showInactivasDetalle, setShowInactivasDetalle] = useState(false);

  const handleSyncPublicaciones = async () => {
    setSyncingPublicaciones(true);
    setPublicacionesProgress(null);
    try {
      await api.post<{ status: string }>('/api/v3/ml/publicaciones/sync', {});
      const poll = setInterval(async () => {
        try {
          const p = await api.get<any>('/api/v3/ml/publicaciones/sync-progress');
          setPublicacionesProgress(p);
          if (!p.running) { clearInterval(poll); setSyncingPublicaciones(false); fetchPublicacionesPage(0, pubSearchInput); }
        } catch {}
      }, 3000);
    } catch (e: any) {
      setSyncingPublicaciones(false);
      toast.addToast('Error: ' + (e?.detail ?? e?.message ?? JSON.stringify(e)), 'error');
    }
  };

  const pubTotalPages = Math.ceil(pubTotal / PUB_PAGE_SIZE);

  const fmtTime = (sec: number) => {
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60), s = sec % 60;
    return `${m}m ${s}s`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    toast.addToast(`Copiado: ${text}`, 'success');
  };

  const publicacionesColumns = [
    {
      key: 'titulo',
      header: 'Publicación',
      width: '40%',
      render: (row: PublicacionItem) => (
        <div className="min-w-0">
          <div
            className="text-xs text-slate-400 font-mono truncate cursor-pointer hover:text-blue-600 hover:underline"
            onClick={(e) => { e.stopPropagation(); copyToClipboard(row.mlaId); }}
            title="Click para copiar MLA"
          >{row.mlaId}{row.variantId ? ` · var ${row.variantId}` : ''}</div>
          <div className="text-sm font-medium text-slate-800 truncate" title={row.titulo ?? ''}>{row.titulo ?? '—'}</div>
          {row.skuInterno && (
            <div
              className="text-xs text-slate-500 truncate cursor-pointer hover:text-blue-600 hover:underline"
              onClick={(e) => { e.stopPropagation(); copyToClipboard(row.skuInterno!); }}
              title="Click para copiar SKU"
            >{row.skuInterno}</div>
          )}
          {row.sincronizadaCon && (
            <div
              className="text-xs text-slate-400 truncate mt-0.5 cursor-pointer hover:text-blue-600 hover:underline"
              title="Click para copiar MLA sincronizada"
              onClick={(e) => { e.stopPropagation(); copyToClipboard(row.sincronizadaCon!); }}
            >
              <span className="text-slate-300 mr-0.5">Sincronizada con</span> 🔗 {row.sincronizadaCon}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'precio',
      header: 'Precio',
      width: '20%',
      align: 'right' as const,
      render: (row: PublicacionItem) => (
        <div>
          {row.precioPromo != null ? (
            <>
              <div className="text-xs text-slate-400 line-through">${Number(row.precio ?? 0).toLocaleString('es-AR')}</div>
              <div className="text-sm font-semibold text-green-700">${Number(row.precioPromo).toLocaleString('es-AR')}</div>
              {row.promoNombre && <div className="text-xs text-green-600 truncate" title={row.promoNombre}>{row.promoNombre}</div>}
            </>
          ) : (
            <div className="text-sm font-medium text-slate-800">{row.precio != null ? `$${Number(row.precio).toLocaleString('es-AR')}` : '—'}</div>
          )}
        </div>
      ),
    },
    {
      key: 'stockDisponibleMl',
      header: 'Stock en ML',
      width: '15%',
      align: 'right' as const,
      render: (row: PublicacionItem) => (
        <div>
          <div className="text-sm font-medium">{row.stockDisponibleMl ?? '—'}</div>
          {row.soldQuantity != null && <div className="text-xs text-slate-400">{row.soldQuantity.toLocaleString('es-AR')} vend.</div>}
        </div>
      ),
    },
    {
      key: 'stockSku',
      header: 'Stock del SKU',
      width: '15%',
      sortable: false,
      filterable: false,
      render: (row: PublicacionItem) => <StockSku stock={row.stock} variante="celda" />,
    },
    {
      key: 'stockMaximo',
      header: 'Tope',
      help: 'Máximo de unidades a publicar',
      width: '12%',
      align: 'right' as const,
      render: (row: PublicacionItem) => (
        <StockMaximoCell row={row} onUpdate={() => refetchPublicaciones()} />
      ),
    },
    {
      key: 'stockSeguridad',
      header: 'No publicar',
      help: 'Unidades que no se publican',
      width: '8%',
      align: 'right' as const,
      render: (row: PublicacionItem) => (
        <StockMaximoCell row={row} campo="stockSeguridad" onUpdate={() => refetchPublicaciones()} />
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      width: '13%',
      align: 'center' as const,
      render: (row: PublicacionItem) => {
        const estado = row.status ?? '';
        // Con variantes, el MLA se repite en varias filas: «Pausar»/«Activar» va sólo en la primera del MLA,
        // y «Reenviar stock ahora» en la primera de cada SKU de ese MLA (cada variante puede ser otro SKU).
        const primera = publicaciones?.find((x) => x.mlaId === row.mlaId) === row;
        const primeraDelSku = publicaciones?.find((x) => x.mlaId === row.mlaId && x.skuInterno === row.skuInterno) === row;
        return (
          <div className="flex flex-col items-center gap-0.5">
            <Badge tone={ESTADO_ML_TONES[estado] ?? 'slate'}>{ESTADO_ML_LABELS[estado] ?? estado ?? '—'}</Badge>
            {primera && <BotonEstadoPublicacion mlaId={row.mlaId} status={row.status ?? null} onCambio={() => refetchPublicaciones()} />}
            {primeraDelSku && row.skuSincroniza && row.skuInterno && <BotonReenviarStock sku={row.skuInterno} todoElSku onHecho={() => refetchPublicaciones()} />}
            {row.pausadaPorSistema && <span className="text-[10px] text-amber-700" title="La pausó el sistema por stock de 1 o menos, para verificar el stock físico">Pausada por stock</span>}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-3">
      {errorPublicaciones && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">
          <span>Error: {errorPublicaciones}</span>
          <button onClick={refetchPublicaciones} className="ml-auto px-2 py-0.5 bg-red-600 text-white rounded text-xs hover:bg-red-700">Reintentar</button>
        </div>
      )}

      {(publicacionesInactivas ?? []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-800">
          <div className="flex items-center gap-2 flex-wrap">
            <span>⚠ {(publicacionesInactivas ?? []).length} SKUs activos sin publicación activa en ML</span>
            <button
              onClick={() => setShowInactivasDetalle(v => !v)}
              className="ml-auto px-2 py-0.5 bg-amber-600 text-white rounded text-xs hover:bg-amber-700"
            >
              {showInactivasDetalle ? 'Ocultar detalle' : 'Ver detalle'}
            </button>
          </div>
          {showInactivasDetalle && (
            <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 text-xs text-amber-700">
              {(publicacionesInactivas ?? []).map(i => (
                <li key={i.skuInterno}>
                  <span className="font-medium">{i.skuInterno}</span>{i.descripcion ? ` — ${i.descripcion}` : ''}{i.mlaId ? ` (${i.mlaId})` : ' (sin mlaId)'}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Traer publicaciones */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-slate-600 font-medium">
            {pubTotal > 0
              ? `${pubTotal.toLocaleString('es-AR')} ${pubTotal === 1 ? 'publicación' : 'publicaciones'}${pubSearch ? (pubTotal === 1 ? ' encontrada' : ' encontradas') : ''}`
              : pubSearch ? 'No se encontraron publicaciones' : 'Presioná "Traer publicaciones de Mercado Libre" para ver tus publicaciones y sus variantes'}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" size="sm" onClick={handleExportAllPublicaciones} loading={exportingAll} disabled={exportingAll || loadingPublicaciones}>
              ↓ Exportar todo
            </Button>
            <Button size="sm" onClick={handleSyncPublicaciones} loading={syncingPublicaciones}>
              {syncingPublicaciones ? 'Trayendo publicaciones...' : '↻ Traer publicaciones de Mercado Libre'}
            </Button>
          </div>
        </div>

        {publicacionesProgress && (
          <div className="space-y-1">
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div className="h-2 rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${publicacionesProgress.pct}%` }} />
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>{publicacionesProgress.processed.toLocaleString('es-AR')} / {publicacionesProgress.total.toLocaleString('es-AR')} ({publicacionesProgress.pct}%)</span>
              <span>{publicacionesProgress.competing} filas</span>
            </div>
            <div className="flex justify-between text-xs text-slate-400">
              <span>Transcurrido: {fmtTime(publicacionesProgress.elapsedSec)}</span>
              {publicacionesProgress.etaSec != null && publicacionesProgress.running && <span>Tiempo restante: ~{fmtTime(publicacionesProgress.etaSec)}</span>}
            </div>
            {!publicacionesProgress.running && !publicacionesProgress.error && (
              <p className="text-xs text-green-600 font-medium">Publicaciones traídas.</p>
            )}
            {publicacionesProgress.error && <p className="text-xs text-red-600">Error: {publicacionesProgress.error}</p>}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={pubSearchInput}
          onChange={(e) => setPubSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handlePubSearch()}
          placeholder="Buscar por MLA, SKU o título..."
          className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <Button size="sm" onClick={handlePubSearch} disabled={loadingPublicaciones}>Buscar</Button>
        {pubSearch && (
          <Button variant="secondary" size="sm" onClick={() => { setPubSearch(''); setPubSearchInput(''); fetchPublicacionesPage(0, ''); }}>
            Limpiar
          </Button>
        )}
      </div>

      <DataTable
        columns={publicacionesColumns}
        data={publicaciones ?? []}
        loading={loadingPublicaciones}
      />

      {pubTotalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-slate-500">
            {pubPage * PUB_PAGE_SIZE + 1}–{Math.min((pubPage + 1) * PUB_PAGE_SIZE, pubTotal)} de {pubTotal.toLocaleString('es-AR')}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={pubPage === 0 || loadingPublicaciones} onClick={() => fetchPublicacionesPage(pubPage - 1)}>← Anterior</Button>
            <Button variant="secondary" size="sm" disabled={pubPage >= pubTotalPages - 1 || loadingPublicaciones} onClick={() => fetchPublicacionesPage(pubPage + 1)}>Siguiente →</Button>
          </div>
        </div>
      )}
    </div>
  );
}
